<template>

    <div class="grid grid-cols-2 lg:grid-cols-6 xl:grid-cols-7 gap-2 py-4 border-b border-gray-200 even:bg-gray-100 hover:bg-gray-200">
       <div class="form-control">
            <div class="flex items-center justify-center">
                <img :src="getProduct(item.sku).meta.image" :alt="getProduct(item.sku).name" class="bg-contain w-24 m-auto flex">
            </div>
        </div>
        <div class="form-control items-center justify-center">
            <h3 class="text-sm font-medium text-gray-800">{{ getProduct(item.sku).name }}</h3>

            <p class="text-sm text-gray-600">
                {{ ellipsis(getProduct(item.sku).meta.description, 36) }}
            </p>
        </div>
        <div class="hidden xl:flex form-control flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2 text-brand-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Digital Gift Code">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            Digital
        </div>
        <div class="form-control flex items-center justify-center">
            <p class="text-xl font-medium">{{ formatCurrency(value.order_currency, item.value) }}</p>
        </div>
        <div class="form-control flex items-center justify-center" :class="{ 'form-control-error': value.fieldHasErrors('id') }">
            {{ item.quantity }}
        </div>
        <div class="flex items-center justify-end p-1">
            <button class="w-full justify-center btn-outline ml-1 text-xs" @click.prevent="toggleItemDetailsModal(index)">
                Edit
            </button>
        </div>
        <div class="flex items-center justify-end p-1">
            <button class="w-full justify-center btn ml-1 text-xs" @click.prevent="removeItem(index)">
                Remove
            </button>
        </div>

        <!-- Order Item Details (MODAL) -->
        <transition
            enter-active-class="ease-out duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div class="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-show="item_details_modal">
                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click.prevent="toggleItemDetailsModal()"></div>

                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                        <div>
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Item Details</h3>

                            <div class="flex">
                                <div class="p-2">
                                    <img :src="getProduct(item.sku).meta.image" :alt="getProduct(item.sku).sku" class="h-24 bg-contain">
                                </div>
                                <div class="flex-1 p-2 text-sm">
                                    <table class="w-full">
                                        <tbody>
                                        <tr>
                                            <td class="py-1 text-left font-medium text-gray-400">Name</td>
                                            <td class="py-1 text-right font-medium">{{ getProduct(item.sku).name }}</td>
                                        </tr>
                                        <tr>
                                            <td class="py-1 text-left font-medium text-gray-400">SKU</td>
                                            <td class="py-1 text-right">{{ getProduct(item.sku).sku }}</td>
                                        </tr>
                                        <tr>
                                            <td class="py-1 text-left font-medium text-gray-400">Currency</td>
                                            <td class="py-1 text-right">{{ getProduct(item.sku).currency }}</td>
                                        </tr>
                                        <tr>
                                            <td class="py-1 text-left font-medium text-gray-400">Discount</td>
                                            <td class="py-1 text-right">{{ getProduct(item.sku).discount / 100 }}%</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="form-control-group col-span-6 xl:col-span-2 py-2" :class="{ 'form-control-error': value.fieldHasErrors('value') }">
                                <label :for="'order_value[' + index + ']'">Value<i></i></label>
                                <div class="input-group">
                                    <span class="font-medium text-gray-600">{{ currencySymbol(getProduct(item.sku).currency) }}</span>
                                    <input type="text" name="order_value" :id="'order_value[' + index + ']'" autocomplete="off" required v-model="item.value">
                                </div>
                                <span v-if="value.fieldHasErrors('value')">{{ value.getFieldError('value') }}</span>
                            </div>

                            <div class="form-control col-span-6 xl:col-span-2 py-2" :class="{ 'form-control-error': value.fieldHasErrors('value') }">
                                <label :for="'order_quantity[' + index + ']'" class="block text-sm font-medium text-gray-700">Quantity<i></i></label>
                                <input type="text" name="order_quantity" :id="'order_quantity[' + index + ']'" autocomplete="off" required v-model="item.quantity">
                                <span v-if="value.fieldHasErrors('quantity')">{{ value.getFieldError('quantity') }}</span>
                            </div>
                        </div>
                        <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-1 sm:gap-3 sm:grid-flow-row-dense">
                            <button class="btn-error justify-center px-6 w-full" :disabled="value.isFormUpdating()" @click.prevent="updateOrderItem">
                                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="value.isFormUpdating()">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Confirm Item Details
                            </button>

                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>

</template>

<script>
import {FormattingMixin} from "../../support/mixins";

export default {
    name: "OrderItem",
    mixins: [FormattingMixin],
    props: {
        value: {
            type: Object,
            required: true
        },
        products: {
            type: Object,
            required: true
        },
        item: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            product: false,
            item_details_modal: false,
        }
    },
    methods: {
        getProduct(sku) {
            if (this.product === false) {
                this.product = this.products.products.data.find(product => product.sku === sku);
            }

            return this.product;
        },
        toggleItemDetailsModal() {
            this.item_details_modal = ! this.item_details_modal;
        },
        removeItem(index) {
            this.value.data.splice(index, 1);
        },
        updateOrderItem() {
            let vm = this;

            vm.value.preflightOrderItem(this.item).then(r => {
                this.item_details_modal = ! this.item_details_modal;
            });
        }
    }
}
</script>


